import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Tooltip,
  useTheme,
  alpha,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks';
import { hasuraClient } from '../../utils/graphqlClient';
import { GET_COMMENTS_QUERY } from '../../graphql/queries/comments';
import {
  CREATE_COMMENT_MUTATION,
  DELETE_COMMENT_MUTATION,
} from '../../graphql/mutations/comments';

const getInitials = (firstName, lastName) => {
  const f = firstName?.[0] || '';
  const l = lastName?.[0] || '';
  return (f + l).toUpperCase() || '?';
};

const formatTime = (dateStr, t) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 1) return t('comments.justNow', 'az önce');
  if (mins < 60) return `${mins} dk önce`;
  if (hours < 24) return `${hours} sa önce`;
  if (days < 7) return `${days} gün önce`;
  return date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const stringToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 50%, 42%)`;
};

const CommentSection = ({ entityType, entityId, sx }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [content, setContent] = useState('');
  const [deletingId, setDeletingId] = useState(null); // confirm için
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isDark = theme.palette.mode === 'dark';

  const fetchComments = useCallback(async () => {
    if (!entityId) return;
    try {
      setLoading(true);
      const data = await hasuraClient.query(GET_COMMENTS_QUERY, {
        entityType,
        entityId: parseInt(entityId),
      });
      setComments(data.comments || []);
    } catch (e) {
      console.error('Error fetching comments:', e);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [comments]);

  const handleSend = useCallback(async () => {
    const trimmed = content.trim();
    if (!trimmed || sending || !user) return;

    setSending(true);
    try {
      const result = await hasuraClient.mutate(CREATE_COMMENT_MUTATION, {
        entityType,
        entityId: parseInt(entityId),
        userId: user.id,
        content: trimmed,
        companyId: user.companyId,
      });
      setComments(prev => [...prev, result.insert_comments_one]);
      setContent('');
    } catch (e) {
      console.error('Error creating comment:', e);
    } finally {
      setSending(false);
    }
  }, [content, sending, user, entityType, entityId]);

  const handleDeleteConfirm = useCallback(async (commentId) => {
    try {
      await hasuraClient.mutate(DELETE_COMMENT_MUTATION, { id: commentId });
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (e) {
      console.error('Error deleting comment:', e);
    } finally {
      setDeletingId(null);
    }
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isWithin10Min = (createdAt) => {
    return (new Date() - new Date(createdAt)) < 10 * 60 * 1000;
  };

  const canDelete = (comment) => {
    if (!user) return false;
    if (!isWithin10Min(comment.created_at)) return false;
    if (user.role === 'ROOT') return true;
    if (user.role === 'DIRECTOR') return true;
    return comment.user_id === user.id;
  };

  const myBubbleBg = theme.palette.primary.main;
  const otherBubbleBg = isDark
    ? alpha(theme.palette.common.white, 0.07)
    : alpha(theme.palette.grey[900], 0.06);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: isDark
          ? alpha(theme.palette.common.black, 0.2)
          : alpha(theme.palette.grey[50], 0.8),
        height: 280,
        ...sx,
      }}
    >
      {/* Messages area */}
      <Box
        ref={messagesContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: alpha(theme.palette.text.primary, 0.12),
            borderRadius: 4,
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress size={24} thickness={3} />
          </Box>
        ) : comments.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
              gap: 1.5,
              color: 'text.disabled',
            }}
          >
            <ChatBubbleOutlineIcon sx={{ fontSize: 36, opacity: 0.4 }} />
            <Typography variant="body2" sx={{ opacity: 0.6 }}>
              {t('comments.empty', 'Henüz yorum yok. İlk yorumu sen yap!')}
            </Typography>
          </Box>
        ) : (
          comments.map((comment, index) => {
            const isMe = comment.user_id === user?.id;
            const fullName = `${comment.user?.first_name || ''} ${comment.user?.last_name || ''}`.trim();
            const avatarColor = stringToColor(fullName || String(comment.user_id));
            const prevComment = comments[index - 1];
            const isSameUserAsPrev = prevComment?.user_id === comment.user_id;
            const showAvatar = !isSameUserAsPrev;

            return (
              <Box
                key={comment.id}
                sx={{
                  display: 'flex',
                  flexDirection: isMe ? 'row-reverse' : 'row',
                  alignItems: 'flex-end',
                  gap: 0.75,
                  mt: isSameUserAsPrev ? 0.25 : 1,
                  '&:hover .comment-delete': { opacity: 1 },
                }}
              >
                {/* Avatar — sadece farklı kullanıcıda göster, aynı kullanıcıda boşluk bırak */}
                <Box sx={{ width: 28, flexShrink: 0, alignSelf: 'flex-end', mb: 0.5 }}>
                  {showAvatar ? (
                    <Tooltip title={fullName} placement={isMe ? 'left' : 'right'}>
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          fontSize: 10,
                          fontWeight: 700,
                          bgcolor: avatarColor,
                          boxShadow: `0 1px 4px ${alpha(avatarColor, 0.45)}`,
                        }}
                      >
                        {getInitials(comment.user?.first_name, comment.user?.last_name)}
                      </Avatar>
                    </Tooltip>
                  ) : null}
                </Box>

                {/* Bubble group */}
                <Box
                  sx={{
                    maxWidth: '72%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: isMe ? 'flex-end' : 'flex-start',
                  }}
                >
                  {!isMe && showAvatar && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: avatarColor,
                        mb: 0.4,
                        px: 0.75,
                        fontWeight: 700,
                        fontSize: 11,
                      }}
                    >
                      {fullName}
                    </Typography>
                  )}

                  {/* Bubble */}
                  <Box
                    sx={{
                      position: 'relative',
                      px: 1.5,
                      py: 0.875,
                      borderRadius: isMe
                        ? isSameUserAsPrev ? '14px 4px 4px 14px' : '14px 4px 14px 14px'
                        : isSameUserAsPrev ? '4px 14px 14px 4px' : '4px 14px 14px 14px',
                      bgcolor: isMe ? myBubbleBg : otherBubbleBg,
                      color: isMe ? 'primary.contrastText' : 'text.primary',
                      boxShadow: isMe
                        ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        : `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
                      wordBreak: 'break-word',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <Typography variant="body2" sx={{ lineHeight: 1.55, fontSize: 13 }}>
                      {comment.content}
                    </Typography>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.disabled',
                      mt: 0.35,
                      px: 0.75,
                      fontSize: 10,
                      lineHeight: 1,
                    }}
                  >
                    {formatTime(comment.created_at, t)}
                  </Typography>
                </Box>

                {/* Delete */}
                {canDelete(comment) && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 2,
                      minWidth: deletingId === comment.id ? 'auto' : 20,
                    }}
                  >
                    {deletingId === comment.id ? (
                      /* Inline onay */
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                          bgcolor: isDark
                            ? alpha(theme.palette.common.black, 0.55)
                            : alpha(theme.palette.common.white, 0.95),
                          border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                          borderRadius: 2,
                          px: 0.75,
                          py: 0.25,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.12)}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontSize: 11, color: 'text.secondary', mr: 0.25 }}
                        >
                          {t('comments.confirmDelete', 'Sil?')}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteConfirm(comment.id)}
                          sx={{
                            p: 0.3,
                            color: 'error.main',
                            '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) },
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeletingId(null)}
                          sx={{
                            p: 0.3,
                            color: 'text.secondary',
                            '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.6) },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Tooltip title={t('comments.deleteTooltip', 'Sil')} placement="top">
                        <IconButton
                          className="comment-delete"
                          size="small"
                          onClick={() => setDeletingId(comment.id)}
                          sx={{
                            opacity: 0,
                            transition: 'opacity 0.15s, color 0.15s',
                            p: 0.4,
                            color: alpha(theme.palette.error.main, 0.5),
                            '&:hover': {
                              color: 'error.main',
                              bgcolor: alpha(theme.palette.error.main, 0.08),
                            },
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          display: 'flex',
          gap: 1,
          alignItems: 'flex-end',
        }}
      >
        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder={t('comments.inputPlaceholder', 'Yorum yaz... (Enter ile gönder)')}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={sending}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2.5,
              fontSize: 13,
              bgcolor: isDark
                ? alpha(theme.palette.common.white, 0.04)
                : alpha(theme.palette.grey[900], 0.03),
              '& fieldset': {
                borderColor: alpha(theme.palette.divider, 0.8),
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!content.trim() || sending}
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            flexShrink: 0,
            bgcolor: content.trim() && !sending ? 'primary.main' : alpha(theme.palette.action.disabled, 0.08),
            color: content.trim() && !sending ? 'white' : 'action.disabled',
            transition: 'background 0.2s, transform 0.1s',
            '&:hover': {
              bgcolor: content.trim() ? 'primary.dark' : undefined,
              transform: content.trim() ? 'scale(1.05)' : undefined,
            },
            '&:active': { transform: 'scale(0.95)' },
          }}
        >
          {sending ? (
            <CircularProgress size={15} sx={{ color: 'inherit' }} />
          ) : (
            <SendIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
};

export default CommentSection;
